import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  deleteDoc,
  query, 
  where,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { getFirebaseDb } from '../firebase';

/**
 * Interface for read receipt data
 */
export interface ReadReceipt {
  id: string;
  userId: string;
  projectId: string;
  screenId: string;
  eventId: string;
  commentId: string;
  readAt: string;
}

/**
 * Get read receipts collection reference
 * Uses root collection readReceipts (not subcollection)
 */
const getReadReceiptsCollection = () => {
  const db = getFirebaseDb();
  return collection(db, 'readReceipts');
};

/**
 * Subscribe to read receipts for a user with real-time updates
 * Returns an unsubscribe function to stop listening
 */
export const subscribeToUserReadReceipts = (
  userId: string,
  onUpdate: (receipts: ReadReceipt[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  const receiptsRef = getReadReceiptsCollection();
  const q = query(receiptsRef, where('userId', '==', userId));
  
  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const receipts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ReadReceipt[];
      onUpdate(receipts);
    },
    (error) => {
      console.error('Error in read receipts subscription:', error);
      if (onError) {
        onError(error as Error);
      }
    }
  );
  
  return unsubscribe;
};

/**
 * Get all read receipts for a user
 */
export const getUserReadReceipts = async (userId: string): Promise<ReadReceipt[]> => {
  try {
    const receiptsRef = getReadReceiptsCollection();
    const q = query(receiptsRef, where('userId', '==', userId));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as ReadReceipt[];
  } catch (error) {
    console.error('Error getting read receipts:', error);
    return [];
  }
};

/**
 * Get read receipts for a specific event
 */
export const getEventReadReceipts = async (userId: string, eventId: string): Promise<ReadReceipt[]> => {
  try {
    const receiptsRef = getReadReceiptsCollection();
    const q = query(receiptsRef, where('userId', '==', userId), where('eventId', '==', eventId));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as ReadReceipt[];
  } catch (error) {
    console.error('Error getting event read receipts:', error);
    return [];
  }
};

/**
 * Mark a comment as read
 * Uses root collection readReceipts with composite ID
 */
export const markCommentAsRead = async (
  userId: string,
  projectId: string,
  screenId: string,
  eventId: string,
  commentId: string
): Promise<boolean> => {
  try {
    const receiptId = `${userId}_${eventId}_${commentId}`;
    const receiptsRef = getReadReceiptsCollection();
    const receiptDoc = doc(receiptsRef, receiptId);
    
    await setDoc(receiptDoc, {
      userId,
      projectId,
      screenId,
      eventId,
      commentId,
      readAt: new Date().toISOString()
    });
    
    return true;
  } catch (error) {
    console.error('Error marking comment as read:', error);
    return false;
  }
};

/**
 * Mark all comments in an event as read
 * Uses root collection readReceipts with composite IDs
 */
export const markAllCommentsAsRead = async (
  userId: string,
  projectId: string,
  screenId: string,
  eventId: string,
  commentIds: string[]
): Promise<boolean> => {
  try {
    const receiptsRef = getReadReceiptsCollection();
    
    const batch = commentIds.map(commentId => {
      const receiptId = `${userId}_${eventId}_${commentId}`;
      const receiptDoc = doc(receiptsRef, receiptId);
      return setDoc(receiptDoc, {
        userId,
        projectId,
        screenId,
        eventId,
        commentId,
        readAt: new Date().toISOString()
      });
    });
    
    await Promise.all(batch);
    return true;
  } catch (error) {
    console.error('Error marking all comments as read:', error);
    return false;
  }
};

/**
 * Check if a specific comment is read by user
 * Uses root collection readReceipts with composite ID
 */
export const isCommentRead = async (
  userId: string,
  eventId: string,
  commentId: string
): Promise<boolean> => {
  try {
    const receiptId = `${userId}_${eventId}_${commentId}`;
    const receiptsRef = getReadReceiptsCollection();
    const receiptDoc = doc(receiptsRef, receiptId);
    const snapshot = await getDoc(receiptDoc);
    
    return snapshot.exists();
  } catch (error) {
    console.error('Error checking if comment is read:', error);
    return false;
  }
};

/**
 * Delete read receipt for a comment
 * Uses root collection readReceipts with composite ID
 */
export const deleteReadReceipt = async (
  userId: string,
  eventId: string,
  commentId: string
): Promise<boolean> => {
  try {
    const receiptId = `${userId}_${eventId}_${commentId}`;
    const receiptsRef = getReadReceiptsCollection();
    const receiptDoc = doc(receiptsRef, receiptId);
    
    await deleteDoc(receiptDoc);
    return true;
  } catch (error) {
    console.error('Error deleting read receipt:', error);
    return false;
  }
};

/**
 * Get set of read comment IDs for an event
 */
export const getReadCommentIds = async (
  userId: string,
  eventId: string
): Promise<Set<string>> => {
  const receipts = await getEventReadReceipts(userId, eventId);
  return new Set(receipts.map(r => r.commentId));
};
